import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { PaperSheet } from './PaperSheet';

export type PolaroidFrameProps = {
  /** Stable per-card index — drives jitter rotation/offset and paper crop. */
  index: number;
  /** Show the wax-pin marker centered above the card. Default true. */
  waxPin?: boolean;
  /** Stable seed override for paper-sheet crop (defaults to `index`). */
  seed?: string | number;
  /** Override base tint passed to PaperSheet (e.g. `var(--color-paper)`). */
  color?: string;
  className?: string;
  children: ReactNode;
};

const JITTER: ReadonlyArray<{ r: number; dx: number; dy: number }> = [
  { r: -4, dx: -2, dy: 0 },
  { r: 3, dx: 0, dy: -2 },
  { r: -2, dx: 3, dy: 1 },
  { r: 4, dx: -1, dy: 2 },
  { r: -3, dx: 2, dy: -1 },
  { r: 5, dx: -2, dy: 1 },
  { r: -5, dx: 1, dy: 0 },
  { r: 2, dx: 0, dy: -2 },
  { r: -3, dx: 3, dy: 1 },
  { r: 4, dx: -2, dy: 2 },
  { r: -2, dx: 1, dy: -1 },
  { r: 3, dx: -3, dy: 1 },
];

/**
 * Polaroid wrapper — motion entrance with stable per-index jitter, paper
 * texture, and an optional wax-pin marker on top. Cards laid out by the
 * cork board (lobby + standalone setup) wrap their content in this so
 * they share the same look + entrance feel.
 */
export const PolaroidFrame = ({
  index,
  waxPin = true,
  seed,
  color,
  className,
  children,
}: PolaroidFrameProps) => {
  const j = JITTER[index % JITTER.length]!;

  return (
    <motion.div
      initial={{ scale: 0.6, opacity: 0, rotate: 0 }}
      animate={{ scale: 1, opacity: 1, rotate: j.r }}
      transition={{ type: 'spring', damping: 16, stiffness: 220, delay: index * 0.04 }}
      className="w-full max-w-[130px] sm:max-w-[180px]"
      style={{ transform: `translate(${j.dx}px, ${j.dy}px)` }}
    >
      <PaperSheet
        seed={seed ?? index}
        color={color}
        className={[
          'relative w-full px-1 pt-1.5 pb-1.5 shadow-[2px_3px_0_rgba(0,0,0,0.18),0_8px_16px_rgba(0,0,0,0.22)]',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {waxPin && (
          <span
            aria-hidden
            className="absolute top-[-7px] left-1/2 z-[2] h-3 w-3 -translate-x-1/2 rounded-full"
            style={{
              background:
                'radial-gradient(circle at 35% 30%, var(--color-und) 0 50%, var(--color-wax-deep) 100%)',
              boxShadow: '0 2px 3px rgba(0,0,0,.5)',
            }}
          />
        )}
        {children}
      </PaperSheet>
    </motion.div>
  );
};
